import {serviceLevelApiEndPoints, clientCompanyProfileEndpoints, serviceLevelAdminEndpoints} from '@configuration';
import {defineStore, storeToRefs} from 'pinia';
import {ref, useRequestURL, createError, computed, useTranslation} from '#imports';
import {readonlyState} from '@malt/utils';
import {
    type AcceptServiceLevelChangeRequest,
    type AdminForceServiceLevelChangeRequest,
    type DeclineServiceLevelChangeRequest,
    type ServiceLevelHistoryResource,
    type ServiceLevelStatusResource,
    type ServiceLevelType,
    type ServiceLevelVerificationErrorType,
} from '@api';
import {useOrganizationDetailsStore} from '~/features/organization-settings/organization-details.store';
import {CompanySizeResource, ServiceLevelVerificationErrorType as ServiceLevelVerificationError} from '@/openapi';

type ServiceLevelStatus = {
    isPhoneNumberMissing: boolean;
    hasBlackListedEmail: boolean;
    needProfessionalEmail: boolean;
    hasOngoingProjects: string | undefined;
    hasPendingRequest: string | undefined;
};

export const useServiceLevelStateStore = defineStore('service-level-state-store', () => {
    const currentUrl = useRequestURL();
    const {t} = useTranslation();
    const {organizationId} = storeToRefs(useOrganizationDetailsStore());

    const pageSource = ref<string | null>();
    const pendingStatusRequest = ref<boolean>(false);

    const serviceLevelStatusResponse = ref<ServiceLevelStatusResource>();

    const currentPlan = computed(() => serviceLevelStatusResponse.value?.currentServiceLevelType);
    const pendingRequest = computed(() => serviceLevelStatusResponse.value?.pendingRequest);
    const serviceLevelChangeRequestInProgress = computed(() => serviceLevelStatusResponse.value?.pendingRequest);
    const lastChangeDate = computed(() => serviceLevelStatusResponse.value?.lastChangeDate);
    const changeHistory = ref<ServiceLevelHistoryResource>();

    function setPendingStatusRequest(boolean: boolean) {
        pendingStatusRequest.value = boolean;
    }

    function setPageSource() {
        pageSource.value = currentUrl.searchParams.get('pageSource');
    }

    async function getServiceLevelStatus(): Promise<void> {
        if (!organizationId.value) {
            throw new Error('Organization details store must be initialized');
        }

        try {
            serviceLevelStatusResponse.value = await serviceLevelApiEndPoints.getStatusOnServiceLevelForOrganization({
                organizationId: organizationId.value,
            });
        } catch {
            throw createError({statusCode: 500, statusMessage: 'Cannot get service level status', fatal: true});
        }
    }

    async function acceptServiceLevelChangeRequest(payload: AcceptServiceLevelChangeRequest) {
        if (!organizationId.value) {
            throw new Error('Organization details store must be initialized');
        }

        serviceLevelStatusResponse.value = await serviceLevelAdminEndpoints.acceptServiceLevelChangeRequest({
            organizationId: organizationId.value,
            acceptServiceLevelChangeRequest: payload,
        });
    }

    async function forceServiceLevelChange(payload: AdminForceServiceLevelChangeRequest) {
        if (!organizationId.value) {
            throw new Error('Organization details store must be initialized');
        }
        serviceLevelStatusResponse.value = await serviceLevelAdminEndpoints.forceServiceLevelChange({
            organizationId: organizationId.value,
            adminForceServiceLevelChangeRequest: payload,
        });
    }

    async function declineServiceLevelChangeRequest(payload: DeclineServiceLevelChangeRequest) {
        if (!organizationId.value) {
            throw new Error('Organization details store must be initialized');
        }

        serviceLevelStatusResponse.value = await serviceLevelAdminEndpoints.declineServiceLevelChangeRequest({
            organizationId: organizationId.value,
            declineServiceLevelChangeRequest: payload,
        });
    }

    async function fetchHistory() {
        if (!organizationId.value) {
            throw new Error('Organization details store must be initialized');
        }

        changeHistory.value = await serviceLevelAdminEndpoints.getServiceLevelHistory({organizationId: organizationId.value});
    }

    const hasPendingRequestText = computed(() =>
        serviceLevelChangeRequestInProgress.value?.direction
            ? t(`client.settings.service.level.panel.${serviceLevelChangeRequestInProgress.value?.direction}.request.received.info`)
            : '',
    );

    const serviceLevelsStatus = computed(() =>
        (serviceLevelStatusResponse.value?.forbiddenTransitions ?? []).reduce(
            (
                acc: Map<ServiceLevelType, ServiceLevelStatus>,
                forbiddenTransitions: {transition: ServiceLevelType; errors: Array<ServiceLevelVerificationErrorType>},
            ) => {
                acc.set(forbiddenTransitions.transition, {
                    hasOngoingProjects: forbiddenTransitions.errors.includes(ServiceLevelVerificationError.OngoingProjects)
                        ? t('service-level.panel.downgrade.blocked.ongoing.projects')
                        : undefined,
                    isPhoneNumberMissing: forbiddenTransitions.errors.includes(ServiceLevelVerificationError.PhoneNumberMissing),
                    needProfessionalEmail: forbiddenTransitions.errors.includes(ServiceLevelVerificationError.PublicEmail),
                    hasBlackListedEmail: forbiddenTransitions.errors.includes(ServiceLevelVerificationError.BlacklistedEmailDomain),
                    hasPendingRequest: forbiddenTransitions.errors.includes(ServiceLevelVerificationError.PendingRequest)
                        ? hasPendingRequestText.value
                        : undefined,
                });
                return acc;
            },
            new Map<ServiceLevelType, ServiceLevelStatus>(),
        ),
    );

    async function getCompanySize(companyId: string): Promise<CompanySizeResource> {
        try {
            const company = await clientCompanyProfileEndpoints.getCompanyProfile({companyId});

            return company.size ?? CompanySizeResource.Individual;
        } catch {
            throw createError({statusCode: 500, statusMessage: 'Cannot get company profile', fatal: true});
        }
    }

    return {
        currentPlan: readonlyState(currentPlan),
        pageSource: readonlyState(pageSource),
        pendingRequest: readonlyState(pendingRequest),
        pendingStatusRequest: readonlyState(pendingStatusRequest),
        serviceLevelChangeRequestInProgress: readonlyState(serviceLevelChangeRequestInProgress),
        changeHistory: readonlyState(changeHistory),
        lastChangeDate,
        acceptServiceLevelChangeRequest,
        declineServiceLevelChangeRequest,
        forceServiceLevelChange,
        serviceLevelsStatus,
        getServiceLevelStatus,
        setPageSource,
        setPendingStatusRequest,
        getCompanySize,
        fetchHistory,
    };
});
